import React, {memo} from "react";
import {get} from "lodash";
import {UserPermissionEnum} from "../../../utils/types/user";

import {BarChart} from "../../../atoms/admin-reporting/atoms/bar-chart";
import {
  AggregationType,
  AnalyticsType,
  ReportingContextType,
} from "../../../store/reporting/reporting.common";
import {RequiredUserPermission} from "../../../atoms/admin-user-permissions/RequiredUserPermission";
import {ReportingContext} from "../ReportingContext";
import {
  ChartProps,
  Props,
  useOnDataPathChange,
  dataProcess_AnalyticsPercentageByFacility,
  numberToPercent,
  withCompare,
} from "./helpers";
import {CompareDateLabel} from "../../../atoms/reporting-compare";
import {CardTitle} from "../../../atoms/card-title";
import {CompareDateLabelsWrapper} from "../reports/Report.common";

const AnalyticsReAdmissionRateByFacilityChart = memo(
  ({data, isFullReport}: ChartProps) => {
    const barData = dataProcess_AnalyticsPercentageByFacility(data);

    return (
      <BarChart
        data={barData.stack}
        max={barData.max}
        labelLeft="Facility"
        labelRight="Percentage"
        full={isFullReport}
        itemsPerRow={1}
        dataLegend={[
          {name: "Residents with Admission greater than 1"},
          {name: "Residents without Admission greater than 1"},
        ]}
        axTickFormat={v => numberToPercent(v)}
        hoverDataPreprocessor={(data, line, index) => {
          const sum = get(
            line.original[index].aggregations,
            AggregationType.SUM,
            0,
          );
          const count = get(
            line.original[index].aggregations,
            AggregationType.COUNT,
            0,
          );

          return [sum, count - sum];
        }}
      />
    );
  },
);

export const RE_ADMISSION_RATE_BY_FACILITY_CHART_ID =
  "re-admission-rate-by-facility" as const;

export const AnalyticsReAdmissionRateByFacility = (props: Props) => (
  <RequiredUserPermission permission={UserPermissionEnum.READMISSION_RATE}>
    <div className="chart-header">
      <CardTitle title="Resident Admission History" subtitle="by Facility" />
      <CompareDateLabelsWrapper>
        <CompareDateLabel dateRange={props.dateRange} global />
      </CompareDateLabelsWrapper>
    </div>
    <ReportingContext
      dataPaths={withCompare({
        what: ReportingContextType.analyticsWithoutResolution,
        type: AnalyticsType.readmissionRateByFacility,
        query: {...props.dateRange},
        facilities: props.facilities,
      })}
      onDataPathChange={useOnDataPathChange(props.reportingBlockSetters)}
      isFullReport={props.reportingBlockSetters.isFullReport}
      enabled={props.reportingBlockSetters.enabled}
    >
      {(data, isFullReport) => (
        <AnalyticsReAdmissionRateByFacilityChart
          data={data}
          isFullReport={isFullReport}
        />
      )}
    </ReportingContext>
  </RequiredUserPermission>
);
